import { Accessor, createContext, createSignal, JSX, Setter, useContext } from 'solid-js';
import { createStore } from 'solid-js/store';
import { idString, type BrowserOrServerId } from 'Browser/BrowserId';
import type { Poster as PosterDesign } from 'Shared/posters/project/posterSchema';
import type { DataRecord } from 'Shared/posters/types';
import type { IThemeInfo } from 'Shared/backend/ThemeInfo';
import type { IStoreWrap } from 'Shared/StoreProvider';
import type { EventDoc } from 'Shared/model/Event';
import type { Special } from 'Shared/model/Specials';
import Assert from 'Common/Assert';
import type * as ImageSizes from 'Shared/model/ImageSizes';

/* The documents that carry a rendered poster */
export type PosterSource = EventDoc | Special;

/* What a page hands the renderer so that it can keep that page's poster rendered */
export interface IPosterRendererConfig {
	sw:            IStoreWrap<PosterSource>;
	themeInfo:     IThemeInfo|undefined;
	page:          string;
	deletingState: string;
	imageFolder:   string;
	imageName:     keyof typeof ImageSizes;
	hasPoster(record:PosterSource): boolean;
	fieldsOnPoster(record:PosterSource): string;
}

/* The image fields of a freshly uploaded poster - see posterImageFields */
export type RenderedPoster = Pick<NonNullable<EventDoc['poster']>,'hash'|'formats'|'dev'> & {isCurrent:true};

export type PosterUpload = {status:'rendering'} | {status:'rendered', poster:RenderedPoster};

export interface IPosterUploadTracker {
	markRendering(docId:BrowserOrServerId): void;
	markRendered(docId:BrowserOrServerId, poster:RenderedPoster): void;
	clearRendering(docId:BrowserOrServerId): void;
}

interface IPosterRendererContext extends IPosterUploadTracker {
	config: Accessor<IPosterRendererConfig|undefined>;
	setConfig: (value:IPosterRendererConfig|undefined) => void;
	setHandover: (fn:()=>void) => void;
	clearHandover: (fn:()=>void) => void;
	detachedRenders: Accessor<IDetachedRender[]>;
	addDetachedRender: (render:Omit<IDetachedRender,'key'>) => Promise<void>;
	removeDetachedRender: (key:string) => void;
	render: Accessor<(()=>Promise<void>)|undefined>;
	setRender: Setter<(()=>Promise<void>)|undefined>;
	uploads: Record<string,PosterUpload|undefined>;
}

/*
	A poster that has outlived the page which was editing it: a deeply-copied description of
	everything needed to mount, capture and upload it, holding no reference back into that page's
	store, resources or DOM. The renderer keeps one of these mounted offscreen until its upload
	lands, which is what lets leaving an event page be instant — nothing has to be captured before
	the navigation is allowed through.
*/
export interface IDetachedRender {
	key:           string;
	docId:         BrowserOrServerId;
	uploadUrl:     string;
	design:        PosterDesign|undefined;
	record:        DataRecord;
	themeInfo:     IThemeInfo|undefined;
}

const PosterRendererContext = createContext<IPosterRendererContext>();

function usePosterRendererContext()
{
	return Assert.have(useContext(PosterRendererContext));
}

export function PosterRendererProvider(P:{children:JSX.Element})
{
	const [config,setConfig] = createSignal<IPosterRendererConfig>();

	/*
		The active renderer's chance to hand its poster to a background render before the page that
		owns the data goes away. Held here, and called from the setter rather than left to the
		renderer's own onCleanup, so that it is guaranteed to run on the one event that matters -
		the config being cleared or replaced. A renderer disposing itself is not the same event:
		moving from one event page straight to another swaps the config while leaving the renderer
		mounted throughout, so a cleanup-based handover would never fire.
	*/
	let handover: (()=>void)|undefined;

	const replaceConfig = (value:IPosterRendererConfig|undefined) => {
		handover?.();
		handover = undefined;
		setConfig(() => value);
	};

	const setHandover = (fn:()=>void) => { handover = fn; };

	/* Identity-checked: a departing renderer's cleanup must not wipe a handover its replacement has
	   already registered. */
	const clearHandover = (fn:()=>void) => { if (handover==fn) handover = undefined; };

	/* Posters still being captured and uploaded after their page has gone. Plain immutable
	   snapshots, so <For> can key on them directly. */
	const [detachedRenders,setDetachedRenders] = createSignal<IDetachedRender[]>([]);
	let nextDetachedKey = 0;

	/* Who is waiting on each render, by key. A render reports itself finished by being removed - it
	   is the one thing that happens whether the capture landed or threw - so the promise resolves
	   there rather than anywhere inside the renderer. Lets a caller with several posters to get
	   through take them one at a time instead of mounting them all at once. */
	const waiting = new Map<string,()=>void>();

	const addDetachedRender = (render:Omit<IDetachedRender,'key'>) => {
		const key = String(nextDetachedKey++);
		setDetachedRenders(renders => [...renders, {...render, key}]);
		return new Promise<void>(resolve => waiting.set(key,resolve));
	};

	const removeDetachedRender = (key:string) => {
		setDetachedRenders(renders => renders.filter(render => render.key!=key));
		waiting.get(key)?.();
		waiting.delete(key);
	};

	/* The active renderer's capture-and-upload, registered here so that pages can ask for a render
	   at a point of their choosing (eg on publishing) without reaching into the renderer. */
	const [render,setRender] = createSignal<(()=>Promise<void>)|undefined>();

	/* Per-event poster render/upload status, keyed by document id. Lives here, above both the
	   event page (which does the rendering) and the events page (which shows the thumbnails), so
	   the events list can react to an upload started while the user was editing. */
	const [uploads,setUploads] = createStore<Record<string,PosterUpload|undefined>>({});

	const markRendering = (docId:BrowserOrServerId) =>
		setUploads(idString(docId),{status:'rendering'});
	const markRendered = (docId:BrowserOrServerId,poster:RenderedPoster) =>
		setUploads(idString(docId),{status:'rendered',poster});
	const clearRendering = (docId:BrowserOrServerId) =>
		setUploads(idString(docId),undefined);

	return (
		<PosterRendererContext.Provider value={{
			config, setConfig:replaceConfig, setHandover, clearHandover,
			detachedRenders, addDetachedRender, removeDetachedRender,
			render, setRender, uploads, markRendering, markRendered, clearRendering
		}}>
			{P.children}
		</PosterRendererContext.Provider>
	);
}

export function usePosterRenderer()
{
	return usePosterRendererContext().config;
}

export function useSetPosterRenderer()
{
	return usePosterRendererContext().setConfig;
}

/* Register what should be handed to a background render when this page's config goes away */
export function usePosterHandover()
{
	const ctx = usePosterRendererContext();
	return {setHandover:ctx.setHandover, clearHandover:ctx.clearHandover};
}

export function useDetachedRenders()
{
	return usePosterRendererContext().detachedRenders;
}

export function useAddDetachedRender()
{
	return usePosterRendererContext().addDetachedRender;
}

export function useRemoveDetachedRender()
{
	return usePosterRendererContext().removeDetachedRender;
}

/* Render the poster now, if there's a renderer mounted and its poster is out of date */
export function useRenderPoster()
{
	const ctx = usePosterRendererContext();
	return async () => await ctx.render()?.();
}

export function useSetRenderPoster()
{
	return usePosterRendererContext().setRender;
}

/*
	Getting this event's poster up to date, for a page that needs the finished render rather than
	just a render started - posting it to social media, where a stale poster is the one that goes out.

	getRenderedPoster() starts a render if the stored poster is stale and returns straight away, so
	the caller can put its own screen up while the capture and upload run. What it is waiting on is
	posterRendering(), and renderedPoster() is what lands.

	Both read the upload tracker rather than a flag of our own: a render may already be running when
	we ask (the renderer's own interval, or a tab switch), in which case getRenderedPoster leaves it
	to finish rather than starting a second - and the tracker is the only thing that knows about it.
*/
export function useGetRenderedPoster()
{
	const ctx = usePosterRendererContext();

	return {
		getRenderedPoster: () => { void ctx.render()?.(); },
		posterRendering: (docId:BrowserOrServerId) => ctx.uploads[idString(docId)]?.status == 'rendering',
		renderedPoster: (docId:BrowserOrServerId) => uploadedPoster(ctx.uploads[idString(docId)])
	};
}

export function usePosterUploads()
{
	return usePosterRendererContext().uploads;
}

export function usePosterUploadTracker()
{
	const ctx = usePosterRendererContext();
	return {markRendering:ctx.markRendering, markRendered:ctx.markRendered, clearRendering:ctx.clearRendering};
}

export function uploadedPoster(upload:PosterUpload|undefined)
{
	return upload?.status == 'rendered' ? upload.poster : undefined;
}
